/** Relations row → **Chat channels** (plan D11): the tour's chat channels — what the
 *  dashboard's Chat Channels tab lists (`/tours/<id>/chat_channels/`), newest travel date
 *  first — in a drawer, with **Unarchive** on an archived channel.
 *
 *  Unarchive (console_tours_manage) asks first, then reopens the channel's archived
 *  conversations through the server (queued to the chat workers; audited there): the list
 *  is refetched, and the row reads "Archived — reopening", without the button, until a
 *  refetch shows it active — across closing and reopening the drawer. State is shape and
 *  words: an archive icon and "Archived", a chat icon and "Active". */
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { Box, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as toursApi from '@/api/tours';
import type { TourChatChannel, TourDetail } from '@/api/tours';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSnackbar } from '@/components/Snackbar';
import { RELATION_PAGE_SIZE, RelationDrawer, RelationFailure, RelationLink, RelationPager } from '@/features/records/relations/RelationDrawer';
import { usePermissions } from '@/hooks/usePermissions';
import { ImmediateActionButton } from '@/features/records/actions/ImmediateActionButton';
import { formatWallDate } from '@/features/records/editor/format';

export const chatChannelsKey = (tourId: number, page: number) => ['tours', tourId, 'chat-channels', page] as const;

export function ChatChannelsRelation({ tour }: { tour: TourDetail }) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  // Channels whose unarchive was queued: kept here (the drawer's list unmounts when it
  // closes), until a refetch shows them no longer archived — so reopening the drawer
  // never offers Unarchive again for a reactivation still on its way.
  const [reopening, setReopening] = useState<ReadonlySet<number>>(() => new Set());
  const first = useQuery({ queryKey: chatChannelsKey(tour.id, 1), queryFn: () => toursApi.listChatChannels(tour.id, 1, RELATION_PAGE_SIZE) });
  return (
    <>
      <RelationLink
        label="Chat channels"
        count={first.data ? first.data.count : null}
        failed={first.isError}
        onOpen={() => {
          setPage(1);
          setOpen(true);
        }}
      />
      <RelationDrawer open={open} onClose={() => setOpen(false)} title={`Chat channels of ${tour.name}`}>
        {open && <ChatChannelList tour={tour} page={page} onPage={setPage} reopening={reopening} setReopening={setReopening} />}
      </RelationDrawer>
    </>
  );
}

function ChatChannelList({
  tour,
  page,
  onPage,
  reopening,
  setReopening,
}: {
  tour: TourDetail;
  page: number;
  onPage: (p: number) => void;
  reopening: ReadonlySet<number>;
  setReopening: Dispatch<SetStateAction<ReadonlySet<number>>>;
}) {
  const { hasPerm } = usePermissions();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<TourChatChannel | null>(null);
  const q = useQuery({
    queryKey: chatChannelsKey(tour.id, page),
    queryFn: () => toursApi.listChatChannels(tour.id, page, RELATION_PAGE_SIZE),
    placeholderData: keepPreviousData,
  });
  // A refetch that shows a reopening channel no longer archived ends its "reopening".
  const rows0 = q.isPlaceholderData ? undefined : q.data?.results;
  useEffect(() => {
    if (!rows0) return;
    const done = rows0.filter((ch) => reopening.has(ch.id) && !ch.is_archived).map((ch) => ch.id);
    if (done.length === 0) return;
    setReopening((cur) => {
      const next = new Set(cur);
      for (const id of done) next.delete(id);
      return next;
    });
  }, [rows0, reopening, setReopening]);
  const unarchive = useMutation({
    mutationFn: (ch: TourChatChannel) => toursApi.unarchiveChatChannel(tour.id, ch.id),
    onSuccess: (r, ch) => {
      setReopening((cur) => new Set(cur).add(ch.id));
      snackbar.show(
        r.queued > 0 ? 'Unarchive requested — the conversations reopen in a moment.' : 'Nothing was archived.',
        'success',
      );
      void qc.invalidateQueries({ queryKey: ['tours', tour.id, 'chat-channels'] });
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Could not unarchive the channel.', 'error'),
  });
  const canUnarchive = hasPerm('console_tours_manage') && !tour.is_deleted;

  if (q.isLoading) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <CircularProgress size={14} aria-hidden /> Loading the chat channels…
      </Typography>
    );
  }
  if (q.isError || !q.data) return <RelationFailure what="the chat channels" onRetry={() => void q.refetch()} />;
  const rows = q.data.results;
  return (
    <>
      <Typography variant="body2" color="text.secondary">
        The channels the dashboard lists for this tour: active ones, and archived ones of past travel dates.
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No chat channels.
        </Typography>
      ) : (
        <Table size="small" aria-label="Chat channels" sx={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '22%' }} />
            <col style={{ width: '44%' }} />
            <col style={{ width: '34%' }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>Travel date</TableCell>
              <TableCell sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>Channel</TableCell>
              <TableCell sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>State</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((ch) => (
              <TableRow key={ch.id} data-chat-channel={ch.id}>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <Typography variant="body2">{ch.travel_date ? formatWallDate(ch.travel_date) : '—'}</Typography>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {ch.name ?? `#${ch.id}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {ch.category_label}
                    {ch.tags.length > 0 ? ` · ${ch.tags.map((t) => t.name ?? `#${t.id}`).join(', ')}` : ''}
                  </Typography>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  {ch.is_archived || reopening.has(ch.id) ? (
                    <Box sx={{ display: 'grid', gap: 0.5, justifyItems: 'start' }}>
                      <Typography variant="body2" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <ArchiveOutlinedIcon aria-hidden sx={{ fontSize: 16, color: 'text.secondary' }} />
                        {reopening.has(ch.id) ? 'Archived — reopening' : 'Archived'}
                      </Typography>
                      {canUnarchive && !reopening.has(ch.id) && (
                        <ImmediateActionButton onClick={() => setConfirm(ch)} disabled={unarchive.isPending} sx={{ py: 0 }}>
                          Unarchive
                        </ImmediateActionButton>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <ChatBubbleOutlineIcon aria-hidden sx={{ fontSize: 16, color: 'primary.main' }} />
                      Active
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <RelationPager page={page} count={q.data.count} shown={rows.length} onPage={onPage} />
      <ConfirmDialog
        open={Boolean(confirm)}
        title="Unarchive this chat channel?"
        message={`The archived conversations of “${confirm?.name ?? ''}” reopen in the chat, so staff and guests on them can write again. The group chat stays archived while the tour starts later than the operator’s activation window. The change is audited.`}
        confirmLabel="Unarchive"
        onConfirm={() => {
          if (confirm) unarchive.mutate(confirm);
          setConfirm(null);
        }}
        onCancel={() => setConfirm(null)}
      />
    </>
  );
}
